/**
 * Describes what the admin can edit on each page. The editor renders its form
 * from this file, so adding a field to a page means adding one entry here and
 * reading it in the page component.
 *
 * Field types: text | textarea | image | icon | strings | list
 *   - `path` is a dot path into the page's content object.
 *   - `list` fields carry `itemFields` (each with a `key`) and an `itemLabel`.
 *   - `strings` is a list of plain text lines.
 */

/** Keep in sync with the `icons` map in components/site/Icon.jsx. */
export const ICON_NAMES = [
  'award', 'badge-check', 'bell-ring', 'building-2', 'cctv', 'clipboard-check', 'clock', 'cpu', 'door-open', 'droplets',
  'factory', 'file-check-2', 'fingerprint', 'fire-extinguisher', 'flame', 'graduation-cap', 'hard-hat', 'heart-handshake',
  'hotel', 'house', 'megaphone', 'pencil-ruler', 'ruler', 'scan-face', 'shield-check', 'shower-head', 'siren', 'sparkles',
  'target', 'trending-down', 'triangle-alert', 'users-round', 'waves', 'wind', 'wrench',
];

export const ICON_OPTIONS = ICON_NAMES.map((value) => ({ value, label: value.replace(/-/g, ' ') }));

const pageBanner = (prefix = 'hero') => ({
  title: 'Page banner',
  fields: [
    { path: `${prefix}.eyebrow`, label: 'Breadcrumb label', type: 'text' },
    { path: `${prefix}.title`, label: 'Heading', type: 'text' },
    { path: `${prefix}.text`, label: 'Intro text', type: 'textarea' },
    { path: `${prefix}.image`, label: 'Background image', type: 'image' },
    { path: `${prefix}.imageAlt`, label: 'Image description (alt text)', type: 'text' },
  ],
});

const heading = (prefix, extra = []) => [
  { path: `${prefix}.eyebrow`, label: 'Small heading above title', type: 'text' },
  { path: `${prefix}.title`, label: 'Title', type: 'text' },
  ...extra,
];

const iconCards = (path, label, itemLabel = 'Card', withText = true) => ({
  path,
  label,
  type: 'list',
  itemLabel,
  itemFields: [
    { key: 'icon', label: 'Icon', type: 'icon' },
    { key: 'title', label: 'Title', type: 'text' },
    ...(withText ? [{ key: 'text', label: 'Text', type: 'textarea' }] : []),
  ],
});

const statList = (path) => ({
  path,
  label: 'Numbers',
  type: 'list',
  itemLabel: 'Number',
  itemFields: [
    { key: 'value', label: 'Big number (e.g. 650+)', type: 'text' },
    { key: 'label', label: 'Caption', type: 'text' },
  ],
});

export const pageSchemas = [
  {
    key: 'home',
    label: 'Home',
    url: '/',
    description: 'Everything on the front page, from the hero to the testimonials.',
    sections: [
      {
        title: 'Hero',
        fields: [
          { path: 'hero.eyebrow', label: 'Small heading above title', type: 'text' },
          { path: 'hero.titleStart', label: 'Title (first part)', type: 'text' },
          { path: 'hero.titleHighlight', label: 'Title (highlighted part)', type: 'text' },
          { path: 'hero.text', label: 'Intro text', type: 'textarea' },
          { path: 'hero.primaryLabel', label: 'Main button label', type: 'text' },
          { path: 'hero.secondaryLabel', label: 'Second button label', type: 'text' },
          { path: 'hero.image', label: 'Background image', type: 'image' },
          { path: 'hero.imageAlt', label: 'Background image description', type: 'text' },
          { path: 'hero.badgeValue', label: 'Badge, big text', type: 'text' },
          { path: 'hero.badgeLabel', label: 'Badge, caption', type: 'text' },
        ],
      },
      { title: 'Numbers band', fields: [statList('stats')] },
      {
        title: 'What we are',
        fields: heading('intro', [
          { path: 'intro.paragraph1', label: 'First paragraph', type: 'textarea' },
          { path: 'intro.paragraph2', label: 'Second paragraph', type: 'textarea' },
          { path: 'intro.image', label: 'Image', type: 'image' },
          { path: 'intro.imageAlt', label: 'Image description', type: 'text' },
          { path: 'intro.steps', label: 'Turnkey steps', type: 'strings', itemLabel: 'Step' },
        ]),
      },
      {
        title: 'Services preview',
        description: 'The cards themselves come from the Services list in the admin.',
        fields: heading('services', [{ path: 'services.text', label: 'Intro text', type: 'textarea' }]),
      },
      { title: 'Benefits', fields: heading('benefits', [iconCards('benefits.items', 'Benefits', 'Benefit')]) },
      { title: 'Why choose us', fields: heading('why', [iconCards('why.items', 'Reasons', 'Reason')]) },
      {
        title: 'Founder',
        fields: [
          { path: 'founder.eyebrow', label: 'Small heading', type: 'text' },
          { path: 'founder.name', label: 'Name', type: 'text' },
          { path: 'founder.role', label: 'Role', type: 'text' },
          { path: 'founder.quote', label: 'Quote', type: 'textarea' },
          { path: 'founder.text', label: 'Short bio', type: 'textarea' },
          { path: 'founder.image', label: 'Photo', type: 'image' },
          { path: 'founder.imageAlt', label: 'Photo description', type: 'text' },
        ],
      },
      { title: 'Clients strip', description: 'Logos come from the Client logos list.', fields: heading('clients') },
      { title: 'Testimonials', description: 'Quotes come from the Testimonials list.', fields: heading('testimonials') },
    ],
  },
  {
    key: 'about',
    label: 'About',
    url: '/about',
    description: 'Company story, the managing director and vision / mission.',
    sections: [
      pageBanner(),
      {
        title: 'Who we are',
        fields: heading('story', [
          { path: 'story.paragraph1', label: 'First paragraph', type: 'textarea' },
          { path: 'story.paragraph2', label: 'Second paragraph', type: 'textarea' },
          { path: 'story.image', label: 'Image', type: 'image' },
          { path: 'story.imageAlt', label: 'Image description', type: 'text' },
        ]),
      },
      {
        title: 'Managing director',
        fields: [
          { path: 'founder.eyebrow', label: 'Small heading', type: 'text' },
          { path: 'founder.name', label: 'Name', type: 'text' },
          { path: 'founder.role', label: 'Role', type: 'text' },
          { path: 'founder.paragraph1', label: 'First paragraph', type: 'textarea' },
          { path: 'founder.paragraph2', label: 'Second paragraph', type: 'textarea' },
          { path: 'founder.image', label: 'Portrait', type: 'image' },
          { path: 'founder.imageAlt', label: 'Portrait description', type: 'text' },
          { path: 'founder.awardImage', label: 'Award photo', type: 'image' },
          { path: 'founder.awardAlt', label: 'Award photo description', type: 'text' },
        ],
      },
      { title: 'Values heading', description: 'The cards reuse “Why choose us” from the Home page.', fields: heading('values') },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    url: '/services',
    description: 'Section headings for fire, security and audit. Individual services live in the Services list.',
    sections: [
      pageBanner(),
      { title: 'Fire fighting', fields: heading('fire', [{ path: 'fire.text', label: 'Intro text', type: 'textarea' }]) },
      {
        title: 'Security systems',
        fields: heading('security', [
          { path: 'security.text', label: 'Intro text', type: 'textarea' },
          { path: 'security.image', label: 'Image', type: 'image' },
          { path: 'security.imageAlt', label: 'Image description', type: 'text' },
        ]),
      },
      {
        title: 'Audit & consultancy',
        fields: heading('audit', [
          { path: 'audit.text', label: 'Intro text', type: 'textarea' },
          { path: 'audit.items', label: 'Audit services', type: 'strings', itemLabel: 'Service' },
          { path: 'audit.image', label: 'Image', type: 'image' },
          { path: 'audit.imageAlt', label: 'Image description', type: 'text' },
        ]),
      },
      {
        title: 'Process',
        fields: heading('process', [
          {
            path: 'process.steps',
            label: 'Steps',
            type: 'list',
            itemLabel: 'Step',
            itemFields: [
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'text', label: 'Text', type: 'textarea' },
            ],
          },
        ]),
      },
    ],
  },
  {
    key: 'amc',
    label: 'AMC',
    url: '/amc',
    description: 'Annual maintenance contract page.',
    sections: [
      pageBanner(),
      {
        title: 'Introduction',
        fields: heading('intro', [
          { path: 'intro.paragraph1', label: 'First paragraph', type: 'textarea' },
          { path: 'intro.paragraph2', label: 'Second paragraph', type: 'textarea' },
          { path: 'intro.image', label: 'Image', type: 'image' },
          { path: 'intro.imageAlt', label: 'Image description', type: 'text' },
        ]),
      },
      { title: 'Highlights', fields: [iconCards('highlights', 'Highlights', 'Highlight')] },
      { title: 'Coverage', fields: heading('coverage', [{ path: 'coverage.items', label: 'Covered items', type: 'strings', itemLabel: 'Item' }]) },
    ],
  },
  {
    key: 'training',
    label: 'Training',
    url: '/training',
    description: 'Training and development programmes.',
    sections: [
      pageBanner(),
      {
        title: 'Why train',
        fields: heading('intro', [
          { path: 'intro.text', label: 'Text', type: 'textarea' },
          { path: 'intro.image', label: 'Main image', type: 'image' },
          { path: 'intro.imageAlt', label: 'Main image description', type: 'text' },
          { path: 'intro.image2', label: 'Second image', type: 'image' },
          { path: 'intro.image2Alt', label: 'Second image description', type: 'text' },
        ]),
      },
      { title: 'Benefits', fields: [iconCards('benefits', 'Benefits', 'Benefit', false)] },
      { title: 'Programmes', fields: heading('programmes', [{ path: 'programmes.items', label: 'Programmes', type: 'strings', itemLabel: 'Programme' }]) },
      { title: 'Numbers', fields: [statList('stats')] },
    ],
  },
  {
    key: 'clients',
    label: 'Clients',
    url: '/clients',
    description: 'Headings for the clientele page. Logos and quotes have their own lists.',
    sections: [
      pageBanner(),
      { title: 'Logo grid', fields: heading('logos') },
      { title: 'Letters of appreciation', fields: heading('letters', [{ path: 'letters.text', label: 'Text', type: 'textarea' }]) },
    ],
  },
  {
    key: 'gallery',
    label: 'Gallery',
    url: '/gallery',
    description: 'The banner above the photo gallery. Photos are managed under Gallery photos.',
    sections: [pageBanner()],
  },
  {
    key: 'contact',
    label: 'Contact',
    url: '/contact',
    description: 'Contact page banner and enquiry form text. Contact details live in Site settings.',
    sections: [
      pageBanner(),
      {
        title: 'Enquiry form',
        fields: [
          { path: 'form.title', label: 'Form title', type: 'text' },
          { path: 'form.text', label: 'Form intro', type: 'textarea' },
        ],
      },
    ],
  },
  {
    key: 'settings',
    label: 'Site settings',
    url: '/',
    description: 'Company details, logo, footer and the call-to-action banner used on every page.',
    sections: [
      {
        title: 'Company details',
        fields: [
          { path: 'company.name', label: 'Company name', type: 'text' },
          { path: 'company.shortName', label: 'Short name', type: 'text' },
          { path: 'company.tagline', label: 'Tagline', type: 'text' },
          { path: 'company.established', label: 'Year established', type: 'text' },
          { path: 'company.phone', label: 'Main phone', type: 'text' },
          { path: 'company.phone2', label: 'Second phone', type: 'text' },
          { path: 'company.whatsapp', label: 'WhatsApp number', type: 'text' },
          { path: 'company.email', label: 'Main email', type: 'text' },
          { path: 'company.email2', label: 'Operations email', type: 'text' },
          { path: 'company.email3', label: 'Other email', type: 'text' },
          { path: 'company.address', label: 'Address', type: 'textarea' },
          { path: 'company.hours', label: 'Working hours', type: 'text' },
          { path: 'company.days', label: 'Working days', type: 'text' },
          { path: 'company.mapLink', label: 'Google Maps link', type: 'text' },
          { path: 'company.mapEmbed', label: 'Google Maps embed URL', type: 'textarea' },
        ],
      },
      {
        title: 'Google reviews',
        description:
          'Live reviews (4★ and above) are pulled from Google automatically when GOOGLE_PLACES_API_KEY is set on the server. The rating and count below are shown on the floating badge whenever live data is unavailable.',
        fields: [
          { path: 'google.rating', label: 'Rating shown on the badge (fallback)', type: 'text' },
          { path: 'google.count', label: 'Number of reviews (fallback)', type: 'text' },
          { path: 'google.mapsUrl', label: 'Google Business listing link', type: 'text' },
          { path: 'google.reviewUrl', label: '“Write a review” link', type: 'text' },
          { path: 'google.showBadge', label: 'Show floating badge (yes / no)', type: 'text' },
          { path: 'google.sectionEyebrow', label: 'Reviews section label', type: 'text' },
          { path: 'google.sectionTitle', label: 'Reviews section title', type: 'text' },
        ],
      },
      {
        title: 'Branding',
        fields: [
          { path: 'brand.logo', label: 'Logo', type: 'image' },
          { path: 'brand.headerSubtitle', label: 'Line under the name in the header', type: 'text' },
          { path: 'brand.topbarText', label: 'Top bar text', type: 'text' },
          { path: 'brand.quoteLabel', label: 'Header button label', type: 'text' },
        ],
      },
      {
        title: 'Footer',
        fields: [
          { path: 'footer.about', label: 'About text', type: 'textarea' },
          { path: 'footer.copyright', label: 'Copyright line', type: 'text' },
        ],
      },
      {
        title: 'Call-to-action banner',
        fields: [
          { path: 'cta.title', label: 'Title', type: 'text' },
          { path: 'cta.text', label: 'Text', type: 'textarea' },
          { path: 'cta.buttonLabel', label: 'Button label', type: 'text' },
        ],
      },
    ],
  },
];

export const pageSchemaByKey = Object.fromEntries(pageSchemas.map((p) => [p.key, p]));
